/**
 * React's entire footprint in the running app: one root, mounted once, over a
 * DOM element the renderer never touches. If this file ever grows a reference
 * to the canvas or to a per-frame value, the boundary rule has been broken
 * (docs/13-architecture.md — React never touches the per-frame path).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { FloorHost } from '../bridge';
import { BuildPalette } from './BuildPalette';
import { Hud } from './Hud';
import { LinePanel } from './LinePanel';
import type { FlyTo } from './SiteFlow';
import { TopBar, type CampaignActions } from './TopBar';
import { Dashboard } from './Dashboard';
import { Technology } from './Technology';
import { Markets } from './Markets';
import { Handbook } from './Handbook';
import { usePanels } from './panels';

/**
 * `flyTo` is the one thing an overlay is allowed to ask of the renderer, and it
 * arrives as a plain callback rather than as a reference to it: the analytics
 * panel names a *cell*, and what the camera does about that is the shell's
 * business. React still holds nothing on the per-frame path.
 */
export function mountHud(host: FloorHost, flyTo: FlyTo, actions: CampaignActions): void {
  const container = document.getElementById('overlays');
  if (!container) throw new Error('overlay root missing from index.html');
  createRoot(container).render(
    <StrictMode>
      <Overlays host={host} flyTo={flyTo} actions={actions} />
    </StrictMode>,
  );
}

/**
 * The whole overlay tree (M1.1): the persistent frame, the floor's own panels
 * (the dock, hideable), the line panel — with Site & flow's calibration and
 * machines as its folds — and the three economy overlays, and the handbook,
 * all on demand.
 */
function Overlays({ host, flyTo, actions }: { host: FloorHost; flyTo: FlyTo; actions: CampaignActions }) {
  const panels = usePanels();
  return (
    <>
      <TopBar host={host} actions={actions} />
      {/* The frame/boundary HUD is debug instrumentation: off by default, a
          click away in the settings menu, still the same numbers B0.6 judged. */}
      {panels.debug && <Hud host={host} />}
      {panels.floorPanels && (
        <>
          <BuildPalette host={host} flyTo={flyTo} />
        </>
      )}
      <LinePanel host={host} flyTo={flyTo} />
      <Dashboard host={host} />
      <Technology host={host} />
      <Markets host={host} />
      <Handbook />
    </>
  );
}
